import styles from "./CalendlyWidget.module.scss";

import { useEffect, useMemo, useState } from "react";

import { InlineWidget, useCalendlyEventListener } from "react-calendly";

import { AppLoadingIndicator } from "@/modules/shared/components/AppLoadingIndicator/AppLoadingIndicator";
import { useIsMobile } from "@/modules/shared/hooks/useIsMobile";

const DEFAULT_HEIGHT = {
  MOBILE: 860,
  DESKTOP: 800,
};

const MARGIN_TOP = {
  DISABLED: 0,
  MOBILE: 16,
  DESKTOP: -40,
};

type CalendlyWidgetProps = {
  url: string;
  onEventScheduled: (eventUri: string, inviteeUri?: string | undefined) => void;
  prefillData: {
    name?: string;
    email?: string;
  };
  utm?: {
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
  };
  disableMarginTop?: boolean;
  onLoadError?: () => void;
};

const LOAD_TIMEOUT_MS = 15_000;

/**
 * @deprecated Legacy Calendly concierge booking. Being replaced by the native
 * Coach Matching flow (MEM-560). Retained only as the migration fallback per
 * MEM-866 — caladan flips a feature flag to revert to this. Remove once Calendly
 * is fully retired ("Out of Calendly" milestone). Do not build new features on it.
 */
export const CalendlyWidget = ({
  url,
  onEventScheduled,
  prefillData,
  utm,
  disableMarginTop = false,
  onLoadError,
}: CalendlyWidgetProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const isMobile = useIsMobile();
  const [height, setHeight] = useState<number | null>(null);

  useCalendlyEventListener({
    onEventScheduled(e) {
      const eventUri = e.data.payload.event.uri;
      const inviteeUri = e.data.payload.invitee.uri;
      onEventScheduled(eventUri, inviteeUri);
    },
    onProfilePageViewed: () => setIsLoaded(true),
    onEventTypeViewed: () => setIsLoaded(true),
    onDateAndTimeSelected: () => setIsLoaded(true),
    onPageHeightResize: (e) => {
      const parsedHeight = Number.parseFloat(e.data.payload.height);
      if (!Number.isNaN(parsedHeight)) {
        setHeight(parsedHeight);
      }
    },
  });

  useEffect(() => {
    if (isLoaded) return;
    const timer = setTimeout(() => {
      onLoadError?.();
    }, LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isLoaded, onLoadError]);

  const minHeight = useMemo(() => {
    const defaultHeight = isMobile
      ? DEFAULT_HEIGHT.MOBILE
      : DEFAULT_HEIGHT.DESKTOP;

    if (height !== null) {
      return height;
    }
    return defaultHeight;
  }, [height, isMobile]);

  const marginTop = useMemo(() => {
    if (disableMarginTop) {
      return MARGIN_TOP.DISABLED;
    }

    if (isMobile) {
      return MARGIN_TOP.MOBILE;
    }

    return MARGIN_TOP.DESKTOP;
  }, [disableMarginTop, isMobile]);

  return (
    <>
      <div
        className={styles.calendlyContainer}
        style={{
          opacity: isLoaded ? 1 : 0,
          transition: "opacity 0.5s ease-in",
        }}
      >
        <InlineWidget
          url={url}
          LoadingSpinner={() => <div />}
          styles={{
            minHeight: minHeight,
            marginTop: marginTop,
          }}
          pageSettings={{
            backgroundColor: "#10100f",
            primaryColor: "#ded287",
            textColor: "#FFFFFF",
          }}
          prefill={prefillData}
          utm={utm}
        />
      </div>
      <div
        className={styles.loaderContainer}
        style={{ display: isLoaded ? "none" : "block" }}
      >
        <AppLoadingIndicator size="medium" color="white" />
      </div>
    </>
  );
};
